// Тема пульта (задача D5): «Система», «Светлая» или «Тёмная».
//
// - Выбор хранится в `localStorage['ai-qual.theme']`. Хранилище может быть закрыто (приватное окно, запрет сайта) или
//   бросать исключение — тогда тема по системе, без ошибки.
// - Тёмная тема — класс `dark` на `<html>` (токены — tokens.css) и `meta[name=color-scheme]`: по нему браузер рисует
//   полосы прокрутки и поля своих окон в той же теме.
// - До отрисовки то же делает встроенный скрипт в `web/index.html`: иначе тёмная тема мигала бы светлой при загрузке.
//   Логика здесь и там одна; правите одно — правьте другое. Если nginx поставит CSP, скрипту нужен свой `sha256`
//   (план E).
import { useSyncExternalStore } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';

export const THEME_PREFS: readonly ThemePref[] = ['system', 'light', 'dark'];

/** Ключ в `localStorage`. */
export const THEME_STORAGE_KEY = 'ai-qual.theme';

/** Подписи для меню пользователя и ⌘K. */
export const THEME_RU: Readonly<Record<ThemePref, string>> = { system: 'Система', light: 'Светлая', dark: 'Тёмная' };

const DARK_QUERY = '(prefers-color-scheme: dark)';

function isThemePref(value: unknown): value is ThemePref {
  return value === 'system' || value === 'light' || value === 'dark';
}

/** Сохранённый выбор; хранилище закрыто или там мусор — «Система». */
export function getThemePref(): ThemePref {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePref(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/** Тёмная ли система сейчас. */
export function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

/** Какая тема получится: «Система» — по системе. */
export function resolveTheme(pref: ThemePref, systemDark: boolean): 'light' | 'dark' {
  if (pref === 'system') return systemDark ? 'dark' : 'light';
  return pref;
}

/** Ставит тему на `<html>`: класс `dark` и `meta[name=color-scheme]`. */
export function applyTheme(pref: ThemePref, systemDark: boolean): void {
  const dark = resolveTheme(pref, systemDark) === 'dark';
  const root = document.documentElement;
  root.classList.toggle('dark', dark);
  let meta = document.querySelector<HTMLMetaElement>('meta[name="color-scheme"]');
  if (meta === null) {
    meta = document.createElement('meta');
    meta.name = 'color-scheme';
    document.head.append(meta);
  }
  meta.content = dark ? 'dark' : 'light';
}

// ───────────────────────────── выбор как внешнее хранилище ─────────────────────────────

const listeners = new Set<() => void>();
let current: ThemePref | null = null;

function snapshot(): ThemePref {
  current ??= getThemePref();
  return current;
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** Запоминает выбор (если хранилище открыто) и сразу ставит тему. */
export function setThemePref(pref: ThemePref): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    // Хранилище закрыто: тема действует до перезагрузки вкладки, ошибки человеку нет.
  }
  current = pref;
  applyTheme(pref, prefersDark());
  notify();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Тема «Система» меняется вместе с системой — без перезагрузки.
  const media = typeof window.matchMedia === 'function' ? window.matchMedia(DARK_QUERY) : null;
  const onSystemChange = (): void => {
    if (snapshot() === 'system') applyTheme('system', prefersDark());
    listener();
  };
  media?.addEventListener('change', onSystemChange);
  return () => {
    listeners.delete(listener);
    media?.removeEventListener('change', onSystemChange);
  };
}

/** Выбор темы для меню: перерисовка при смене выбора и при смене системной темы. */
export function useThemePref(): ThemePref {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

function resolvedSnapshot(): 'light' | 'dark' {
  return resolveTheme(snapshot(), prefersDark());
}

/** Тема, которая сейчас на экране: для Sonner и для всего, что рисуется не токенами. */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribe, resolvedSnapshot, resolvedSnapshot);
}

/** Начальное состояние — для тестов: выбор читается из хранилища заново. */
export function resetThemeForTests(): void {
  current = null;
  document.documentElement.classList.remove('dark');
}
